// Provider per entity: NL/DE Twinfield, BE Exact (invoices only), UK Xero. Mirrors server/src/utils/accountingRouter.js.
export const AccountingProvider = {
    TWINFIELD: 'twinfield',
    EXACT: 'exact',
    XERO: 'xero',
}

// Identifier column per provider per record type; Peppol rides on the Exact row, it is not a provider.
export const ACCOUNTING_IDENTIFIERS = {
    [AccountingProvider.TWINFIELD]: { label: 'Twinfield', field: 'twinfield_id' },
    [AccountingProvider.EXACT]: {
        label: 'Exact Online',
        field: 'exact_online_identifier',
        secondaryLabel: 'Peppol',
        secondaryField: 'peppol_identifier',
    },
    [AccountingProvider.XERO]: { label: 'Xero', field: 'xero_contact_id' },
}

export const ACCOUNTING_INVOICE_IDENTIFIERS = {
    [AccountingProvider.TWINFIELD]: { label: 'Twinfield Invoice', field: 'twinfield_id' },
    [AccountingProvider.EXACT]: { label: 'Exact Invoice', field: 'exact_invoice_identifier' },
    [AccountingProvider.XERO]: { label: 'Xero Invoice', field: 'xero_invoice_id' },
}

export const ACCOUNTING_BILL_IDENTIFIERS = {
    [AccountingProvider.TWINFIELD]: { label: 'Twinfield Bill', field: 'twinfield_id' },
    [AccountingProvider.XERO]: { label: 'Xero Bill', field: 'xero_bill_id' },
}

const PROVIDERS_BY_ENTITY = {
    1: [AccountingProvider.TWINFIELD],                                                  // NL
    2: [AccountingProvider.TWINFIELD],                                                  // DE — on Twinfield today; a dedicated system is expected from the client
    3: [AccountingProvider.EXACT],                                                      // BE — Exact Online (which carries Peppol delivery)
    4: [AccountingProvider.XERO],                                                       // UK — cutover, Xero replaces Twinfield
}

/** Entities whose documents are routed to Xero instead of Twinfield. */
export const XERO_ENTITY_IDS = Object.entries(PROVIDERS_BY_ENTITY)
    .filter(([, providers]) => providers.includes(AccountingProvider.XERO))
    .map(([id]) => Number(id))

export const accountingProvidersFor = (entityId) => (
    PROVIDERS_BY_ENTITY[Number(entityId)] ?? [AccountingProvider.TWINFIELD]
)

export const isXeroEntity = (entityId) => (
    accountingProvidersFor(entityId).includes(AccountingProvider.XERO)
)

// Legacy: Exact once routed on customer country. Routing is now per entity; kept for reference only.
export const EXACT_PEPPOL_COUNTRIES = ['BE']

export const isExactPeppolCustomer = (countryCode) => (
    EXACT_PEPPOL_COUNTRIES.includes(String(countryCode || '').toUpperCase())
)

/** Whether the UI offers Twinfield actions — BE is excluded, though the server still posts BE bills there. */
export const usesTwinfield = (entityId) => (
    accountingProvidersFor(entityId).includes(AccountingProvider.TWINFIELD)
)

/** Where payments are read back from — not the same question as which system holds the document. */
export const paymentsFromTwinfield = (entityId) => !isXeroEntity(entityId)

/** Bill provider. Exact has no purchase-bill side, so BE bills stay on Twinfield like NL and DE. */
export const billAccountingProvider = (entityId) => (
    isXeroEntity(entityId) ? AccountingProvider.XERO : AccountingProvider.TWINFIELD
)

/** Invoice provider, keyed on the entity: BE goes to Exact whoever the customer is. */
export const invoiceAccountingProviders = (entityId) => {
    if (isXeroEntity(entityId)) return [AccountingProvider.XERO]
    if (accountingProvidersFor(entityId).includes(AccountingProvider.EXACT)) {
        return [AccountingProvider.EXACT]
    }
    return [AccountingProvider.TWINFIELD]
}

/** Identifier rows for one invoice, resolved by the rule above rather than by entity alone. */
export const invoiceAccountingIdentifiers = (invoice, entityId) => {
    const expected = invoiceAccountingProviders(entityId)
    const pool = Object.keys(ACCOUNTING_INVOICE_IDENTIFIERS)

    const row = (key, expectedForInvoice) => ({
        key,
        ...ACCOUNTING_INVOICE_IDENTIFIERS[key],
        value: invoice?.[ACCOUNTING_INVOICE_IDENTIFIERS[key].field] || null,
        expected: expectedForInvoice,
    })

    return [
        ...expected.filter((key) => pool.includes(key)).map((key) => row(key, true)),
        ...pool
            .filter((key) => !expected.includes(key) && invoice?.[ACCOUNTING_INVOICE_IDENTIFIERS[key].field])
            .map((key) => row(key, false)),
    ]
}

/** Identifier rows for a customer/vendor: expected providers (blank = "Not synced"), then any populated-but-unexpected one. */
export const accountingIdentifiersFor = (record, entityId, { identifiers = ACCOUNTING_IDENTIFIERS } = {}) => {
    // Pool = the columns that record type actually has; an invoice has no Peppol identifier.
    const pool = Object.keys(identifiers)
    const expected = accountingProvidersFor(entityId).filter((key) => pool.includes(key))

    const row = (key, expectedForEntity) => ({
        key,
        ...identifiers[key],
        value: record?.[identifiers[key].field] || null,
        secondaryValue: identifiers[key].secondaryField
            ? record?.[identifiers[key].secondaryField] || null
            : null,
        expected: expectedForEntity,
    })

    return [
        ...expected.map((key) => row(key, true)),
        ...pool
            .filter((key) => !expected.includes(key) && record?.[identifiers[key].field])
            .map((key) => row(key, false)),
    ]
}
